import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Board-level notices (ADR-055 phase 6b): a message that must outlive the dialog that caused it. A dialog reports its
 * own failures inline while it is open; only a failure that lands AFTER the dialog is gone (the user went Back, or
 * closed it mid-upload) comes here — so nothing is reported twice and nothing is lost (diff-panel follow-up of phase 5).
 */
interface Notices {
  report: (message: string) => void;
}
const NoticesContext = createContext<Notices>({ report: () => undefined });
export const usePmNotices = () => useContext(NoticesContext);

export function PmNoticesProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Array<{ id: number; message: string }>>([]);
  const next = useRef(1);
  const report = useCallback((message: string) => setItems((list) => [...list, { id: next.current++, message }]), []);
  return (
    <NoticesContext.Provider value={{ report }}>
      {items.length > 0 ? (
        <section aria-label={t("pmBoard.notices.label")} className="flex flex-col gap-2 px-4 pt-4 sm:px-8">
          {items.map((n) => (
            <div key={n.id} role="alert" className="border-destructive/40 bg-background text-destructive flex items-start gap-2 rounded-lg border px-3 py-2 text-sm">
              <span className="flex-1">{n.message}</span>
              <Button variant="ghost" size="icon-xs" aria-label={t("pmBoard.notices.dismiss")} onPress={() => setItems((list) => list.filter((x) => x.id !== n.id))}>
                <X aria-hidden="true" />
              </Button>
            </div>
          ))}
        </section>
      ) : null}
      {children}
    </NoticesContext.Provider>
  );
}

/** `true` while the calling component is mounted — so async work can tell "my dialog is still open" from "gone". */
export function useMountedRef() {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
}
